import React, { useState } from 'react';
import ParticleBackground from './ParticleBackground';

interface PasswordAuthProps {
  onAuthenticated: () => void;
  onBack: () => void;
}

const PasswordAuth: React.FC<PasswordAuthProps> = ({
  onAuthenticated,
  onBack,
}) => {
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password.trim()) {
      setError('Please enter a password');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch('/api/v1/auth/validate-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();

      if (data.success) {
        onAuthenticated();
      } else {
        setError(data.error || 'Invalid password');
      }
    } catch (err: any) {
      console.error('Authentication error:', err);
      setError('Failed to authenticate. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='min-h-screen bg-background-light dark:bg-background-dark flex items-center justify-center relative overflow-hidden'>
      {/* Animated gradient background - same as splash page */}
      <div
        className='fixed inset-0 overflow-hidden'
        style={{
          zIndex: 0,
          backgroundColor: '#ffffff',
          pointerEvents: 'none',
        }}
      >
        <div className='absolute w-full h-full opacity-55'>
          {/* Multiple overlapping radial gradients to create mesh effect */}
          <div
            className='absolute w-full h-full'
            style={{
              background: `
                radial-gradient(circle at 20% 20%, #2A7B9B 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, #57C785 0%, transparent 50%),
                radial-gradient(circle at 20% 80%, #EDDD53 0%, transparent 50%),
                radial-gradient(circle at 50% 50%, #2A7B9B 0%, transparent 60%)
              `,
              animation: 'gradient-shift 20s ease infinite',
            }}
          />
          <div
            className='absolute w-full h-full'
            style={{
              background: `
                radial-gradient(circle at 40% 10%, #EDDD53 0%, transparent 40%),
                radial-gradient(circle at 90% 40%, #57C785 0%, transparent 45%),
                radial-gradient(circle at 60% 90%, #2A7B9B 0%, transparent 45%),
                radial-gradient(circle at 70% 30%, #57C785 0%, transparent 35%)
              `,
              animation: 'gradient-shift 25s ease infinite reverse',
            }}
          />
          <div
            className='absolute w-full h-full'
            style={{
              background: `
                radial-gradient(circle at 85% 15%, #EDDD53 0%, transparent 35%),
                radial-gradient(circle at 35% 85%, #2A7B9B 0%, transparent 40%),
                radial-gradient(circle at 65% 65%, #57C785 0%, transparent 30%)
              `,
              animation: 'gradient-shift 30s ease infinite',
            }}
          />
        </div>
      </div>

      {/* Particles layer */}
      <ParticleBackground isDark={false} />

      {/* Content */}
      <div className='text-center p-8 relative z-10'>
        <div className='max-w-md mx-auto bg-surface-light/80 dark:bg-surface-dark/80 backdrop-blur-sm border border-border-light dark:border-border-dark rounded-lg p-8'>
          <h1 className='text-3xl font-bold text-text-primary-light dark:text-text-primary-dark mb-2'>
            Access Required
          </h1>
          <p className='text-text-secondary-light dark:text-text-secondary-dark mb-6'>
            Please enter your password to continue
          </p>

          <form onSubmit={handleSubmit} className='space-y-4'>
            <div>
              <input
                type='password'
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder='Enter password'
                className='w-full px-4 py-3 bg-surface-light/50 dark:bg-surface-dark/50 border border-border-light dark:border-border-dark rounded-lg text-text-primary-light dark:text-text-primary-dark placeholder-text-secondary-light dark:placeholder-text-secondary-dark focus:outline-none focus:ring-2 focus:ring-[#2A7B9B] focus:border-transparent'
                disabled={isLoading}
                autoFocus
              />
            </div>

            {error && <div className='text-red-500 text-sm'>{error}</div>}

            <div className='flex gap-3'>
              <button
                type='button'
                onClick={onBack}
                className='flex-1 px-4 py-3 text-text-secondary-light dark:text-text-secondary-dark border border-border-light dark:border-border-dark rounded-lg hover:bg-surface-light/50 dark:hover:bg-surface-dark/50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#2A7B9B] focus:ring-offset-2'
                disabled={isLoading}
              >
                Back
              </button>
              <button
                type='submit'
                className='flex-1 px-4 py-3 bg-[#2A7B9B] text-white rounded-lg hover:bg-[#1f5f7a] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#2A7B9B] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'
                disabled={isLoading}
              >
                {isLoading ? 'Verifying...' : 'Continue'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PasswordAuth;
